import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { AlertTriangle, Check, Loader2, Pencil, RefreshCw, Sparkles, Unlink, EyeOff, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { CompanySearch, type CompanyOption } from '@/components/CompanySearch';
import { cn } from '@/lib/utils';
import {
  useAcceptCippMatches,
  useCippTenantMap,
  useClearCippMapping,
  useSetCippMapping,
  useStartCippMatching,
  type CippMappingInput,
  type CippMatchCandidate,
  type CippMatchReason,
  type CippTenantMapRow,
} from '@/api/cipp';

/**
 * Admin → CIPP mapping. Links each Microsoft 365 tenant in CIPP to its Autotask
 * company. CIPP has no Autotask integration, so Cosmos keeps this map itself; the
 * server scores likely companies (contact email domains, website, name) and an
 * admin confirms. Nothing is mapped automatically.
 */

type View = 'review' | 'mapped' | 'ignored' | 'all';

const VIEWS: { id: View; label: string }[] = [
  { id: 'review',  label: 'Needs review' },
  { id: 'mapped',  label: 'Mapped' },
  { id: 'ignored', label: 'Ignored' },
  { id: 'all',     label: 'All' },
];

const isUnmapped = (r: CippTenantMapRow) => !r.mapping;

export default function AdminCippPage() {
  const { data, isLoading, error, refetch, isFetching } = useCippTenantMap();
  const startMatching = useStartCippMatching();
  const accept = useAcceptCippMatches();
  const [view, setView] = useState<View>('review');
  const [search, setSearch] = useState('');

  const rows = data?.items ?? [];
  const counts = useMemo(() => ({
    review: rows.filter(isUnmapped).length,
    mapped: rows.filter((r) => r.mapping && !r.mapping.ignored).length,
    ignored: rows.filter((r) => r.mapping?.ignored).length,
    all: rows.length,
  }), [rows]);

  const strongUnmapped = rows.filter((r) => isUnmapped(r) && r.suggestion?.strong && r.suggestion.candidates[0]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) =>
        view === 'all' ? true
        : view === 'review' ? isUnmapped(r)
        : view === 'mapped' ? r.mapping && !r.mapping.ignored
        : r.mapping?.ignored)
      .filter((r) => !q || [r.displayName, r.defaultDomainName, r.mapping?.companyName ?? '', r.suggestion?.candidates[0]?.companyName ?? '']
        .some((s) => s.toLowerCase().includes(q)));
  }, [rows, view, search]);

  const job = data?.job;

  async function acceptStrong() {
    const items = strongUnmapped.map((r) => {
      const c = r.suggestion!.candidates[0];
      return { customerId: r.customerId, companyId: c.companyId, companyName: c.companyName };
    });
    try {
      const res = await accept.mutateAsync(items);
      toast.success(`Mapped ${res.saved} tenant${res.saved !== 1 ? 's' : ''}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the mappings');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">CIPP tenant mapping</h1>
          <p className="max-w-2xl text-xs text-muted-foreground">
            Link each Microsoft 365 tenant to its Autotask company. CIPP has no Autotask integration, so Cosmos
            suggests matches from contact email domains, company websites and names — confirm each one.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => startMatching.mutate()}
            disabled={job?.running || startMatching.isPending}
          >
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
            {job?.running ? `Matching ${job.done}/${job.total || '…'}` : 'Re-run matching'}
          </Button>
          <Button
            size="sm"
            onClick={acceptStrong}
            disabled={strongUnmapped.length === 0 || accept.isPending}
            title="Map every unmapped tenant that has a strong suggested match"
          >
            {accept.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1.5 h-3.5 w-3.5" />}
            Accept {strongUnmapped.length} strong match{strongUnmapped.length !== 1 ? 'es' : ''}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {job?.running && (
        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Scoring tenants against Autotask ({job.done} of {job.total || '…'}). Suggestions appear as each tenant finishes
          — this takes a couple of minutes.
        </div>
      )}
      {job?.error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          Matching failed: {job.error}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 rounded-lg bg-muted p-1">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setView(v.id)}
              className={cn(
                'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                view === v.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {v.label} <span className="ml-1 tabular-nums text-muted-foreground">{counts[v.id]}</span>
            </button>
          ))}
        </div>
        <div className="w-64">
          <SearchInput value={search} onValueChange={setSearch} placeholder="Filter tenants or companies…" withIcon className="h-8 text-sm" />
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading tenants from CIPP…
        </div>
      )}
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Failed to load tenants: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {data && (
        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <th className="w-[28%] px-4 py-2.5">Tenant</th>
                <th className="w-[26%] px-4 py-2.5">Autotask company</th>
                <th className="px-4 py-2.5">Suggested match</th>
                <th className="w-[1%] px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {visible.map((r) => <TenantRow key={r.customerId} row={r} jobRunning={!!job?.running} />)}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    {view === 'review' && !search ? 'Every tenant is mapped or ignored.' : 'No tenants match.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TenantRow({ row, jobRunning }: { row: CippTenantMapRow; jobRunning: boolean }) {
  const setMapping = useSetCippMapping();
  const clearMapping = useClearCippMapping();
  const [editing, setEditing] = useState(false);
  const busy = setMapping.isPending || clearMapping.isPending;

  const { mapping, suggestion } = row;
  const top = suggestion?.candidates[0];
  // Show suggestions for unmapped tenants, and for mapped ones where we'd pick something else.
  const showSuggestion = !mapping || (!mapping.ignored && top && top.companyId !== mapping.companyId);

  async function save(value: CippMappingInput, done?: string) {
    try {
      await setMapping.mutateAsync({ customerId: row.customerId, value });
      setEditing(false);
      if (done) toast.success(done);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the mapping');
    }
  }

  async function unmap() {
    try {
      await clearMapping.mutateAsync(row.customerId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not clear the mapping');
    }
  }

  const pick = (c: CippMatchCandidate) => save({ companyId: c.companyId, companyName: c.companyName }, `${row.displayName} → ${c.companyName}`);

  return (
    <tr className={cn('align-top hover:bg-muted/30', busy && 'opacity-60')}>
      <td className="px-4 py-3">
        <div className="font-medium">{row.displayName}</div>
        <div className="text-xs text-muted-foreground">{row.defaultDomainName}</div>
        {!row.healthy && (
          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-medium text-destructive">
            <AlertTriangle className="h-3 w-3" /> CIPP can't reach this tenant
          </span>
        )}
      </td>

      <td className="px-4 py-3">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <CompanySearch
              value={null}
              onChange={(c: CompanyOption | null) => { if (c) void save({ companyId: c.id, companyName: c.name }); }}
              placeholder="Search Autotask…"
              className="flex-1"
            />
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setEditing(false)} aria-label="Cancel">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : mapping?.ignored ? (
          <span className="text-xs italic text-muted-foreground">Ignored — not linked to a company</span>
        ) : mapping ? (
          <>
            <div className="font-medium">{mapping.companyName}</div>
            <div className="text-xs text-muted-foreground">
              #{mapping.companyId} · {mapping.mappedBy.split('@')[0]} · {format(new Date(mapping.mappedAt), 'd MMM yyyy')}
            </div>
          </>
        ) : (
          <span className="text-xs text-muted-foreground">Not mapped</span>
        )}
      </td>

      <td className="px-4 py-3">
        {!showSuggestion ? null
          : suggestion?.error ? (
            <span className="text-xs text-destructive">Matching failed: {suggestion.error}</span>
          ) : !suggestion ? (
            <span className="text-xs text-muted-foreground">{jobRunning ? 'Scoring…' : '—'}</span>
          ) : !top ? (
            <span className="text-xs text-muted-foreground">No likely company found</span>
          ) : (
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-medium">{top.companyName}</span>
                <span
                  className={cn(
                    'rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                    suggestion.strong ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning',
                  )}
                >
                  {suggestion.strong ? 'Strong' : 'Possible'}
                </span>
              </div>
              <Reasons reasons={top.reasons} />
              {suggestion.candidates.length > 1 && (
                <div className="text-xs text-muted-foreground">
                  or{' '}
                  {suggestion.candidates.slice(1).map((c, i) => (
                    <span key={c.companyId}>
                      {i > 0 && ' · '}
                      <button type="button" className="underline-offset-2 hover:text-foreground hover:underline" onClick={() => pick(c)} disabled={busy}>
                        {c.companyName}
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
      </td>

      <td className="whitespace-nowrap px-4 py-3">
        <div className="flex justify-end gap-1">
          {showSuggestion && top && !editing && (
            <Button size="sm" className="h-7" onClick={() => pick(top)} disabled={busy}>
              <Check className="mr-1 h-3.5 w-3.5" /> {mapping ? 'Use this' : 'Accept'}
            </Button>
          )}
          {!editing && (
            <Button variant="outline" size="sm" className="h-7" onClick={() => setEditing(true)} disabled={busy}>
              <Pencil className="mr-1 h-3.5 w-3.5" /> {mapping && !mapping.ignored ? 'Change' : 'Choose'}
            </Button>
          )}
          {!mapping && !editing && (
            <Button variant="ghost" size="sm" className="h-7 text-muted-foreground" onClick={() => save({ ignored: true })} disabled={busy} title="Not a client tenant — stop suggesting a company">
              <EyeOff className="mr-1 h-3.5 w-3.5" /> Ignore
            </Button>
          )}
          {mapping && !editing && (
            <Button variant="ghost" size="sm" className="h-7 text-muted-foreground" onClick={unmap} disabled={busy} title="Remove this mapping">
              <Unlink className="mr-1 h-3.5 w-3.5" /> {mapping.ignored ? 'Un-ignore' : 'Unmap'}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function Reasons({ reasons }: { reasons: CippMatchReason[] }) {
  // Collapse per-domain contact hits into one total; list the domains in the tooltip.
  const contacts = reasons.filter((r) => r.kind === 'contacts');
  const contactTotal = contacts.reduce((n, r) => n + (r.count ?? 0), 0);
  const website = reasons.find((r) => r.kind === 'website');
  const name = reasons.some((r) => r.kind === 'name');

  const chip = 'rounded border border-border bg-muted/50 px-1.5 py-0.5 text-[11px] text-muted-foreground';
  return (
    <div className="flex flex-wrap gap-1">
      {contactTotal > 0 && (
        <span className={chip} title={contacts.map((r) => `${r.count} @${r.domain}`).join(', ')}>
          {contactTotal} contact{contactTotal !== 1 ? 's' : ''} @{contacts[0].domain}{contacts.length > 1 ? ` +${contacts.length - 1}` : ''}
        </span>
      )}
      {website && <span className={chip}>website {website.domain}</span>}
      {name && <span className={chip}>similar name</span>}
    </div>
  );
}
